import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import StatusBanner from '../../components/ui/StatusBanner';
import {
  EmployeeDashboardGrid,
  EmployeeDetailList,
  EmployeeSummaryCard,
  EmployeeWorkspaceEmptyState,
  EmployeeWorkspaceSection
} from '../../components/workforce/EmployeeWorkspace';
import { buildPersonWorkspacePath } from '../../config/workforceRouting';
import { apiRequest } from '../../services/apiClient';
import { loadOrganization } from '../../services/workforceOrganizationApi';

const EMPTY_REPORTS = Object.freeze({ status: 'idle', items: [], error: '' });

function displayName(profile) {
  return profile?.preferredName
    || [profile?.firstName, profile?.lastName].filter(Boolean).join(' ').trim()
    || [profile?.managerFirstName, profile?.managerLastName].filter(Boolean).join(' ').trim()
    || (profile?.id ? `Employee #${profile.id}` : 'Not available');
}

function resolvedLocationId(profile) {
  const value = Number(profile?.managerLocationId ?? profile?.locationId);
  return Number.isFinite(value) && value > 0 ? value : null;
}

export default function EmployeeOrganizationWorkspace({
  profile,
  manager = null,
  accessibleLocations = [],
  locationsLoading = false
}) {
  const profileId = Number(profile?.id);
  const companyId = Number(profile?.companyId);
  const supervisorId = Number(profile?.supervisorEmployeeId);
  const hasProfile = profileId > 0 && companyId > 0;
  const [reports, setReports] = useState(hasProfile ? { ...EMPTY_REPORTS, status: 'loading' } : EMPTY_REPORTS);
  const [supervisor, setSupervisor] = useState({ status: supervisorId > 0 ? 'loading' : 'idle', profile: null, error: '' });

  const locationNames = useMemo(
    () => new Map(accessibleLocations.map(location => [Number(location.id), location.code || location.name || `Location #${location.id}`])),
    [accessibleLocations]
  );

  useEffect(() => {
    if (!hasProfile) return undefined;
    const controller = new AbortController();
    let cancelled = false;
    setReports({ ...EMPTY_REPORTS, status: 'loading' });
    loadOrganization({ rootProfileId: profileId, depth: 1, status: 'any', signal: controller.signal })
      .then(items => {
        if (cancelled) return;
        setReports({ status: 'loaded', items, error: '' });
      })
      .catch(error => {
        if (!cancelled) setReports({ status: error?.status === 403 ? 'forbidden' : 'error', items: [], error: error?.message || 'Unable to load direct reports.' });
      });
    return () => { cancelled = true; controller.abort(); };
  }, [companyId, hasProfile, profileId]);

  useEffect(() => {
    if (!(supervisorId > 0)) {
      setSupervisor({ status: 'idle', profile: null, error: '' });
      return undefined;
    }
    const controller = new AbortController();
    let cancelled = false;
    setSupervisor({ status: 'loading', profile: null, error: '' });
    apiRequest(`/api/employees/profiles/${supervisorId}`, { signal: controller.signal })
      .then(data => {
        if (!cancelled) setSupervisor({ status: 'loaded', profile: data?.profile || null, error: '' });
      })
      .catch(error => {
        if (!cancelled) setSupervisor({ status: error?.status === 403 ? 'forbidden' : 'error', profile: null, error: error?.message || 'Unable to load the reporting manager.' });
      });
    return () => { cancelled = true; controller.abort(); };
  }, [supervisorId]);

  const directReports = reports.items;
  const currentLocationId = resolvedLocationId({ ...profile, managerLocationId: manager?.locationId });
  const currentLocation = manager?.locationCode || locationNames.get(currentLocationId) || null;
  const departments = [...new Set(directReports.map(item => item.department).filter(Boolean))];
  const locations = [...new Set(directReports.map(item => item.locationCode || item.locationName || locationNames.get(resolvedLocationId(item))).filter(Boolean))];

  return (
    <div className="employee-organization" data-testid="employee-organization-workspace">
      <EmployeeWorkspaceSection title="Organization summary" description="Authoritative employee-profile organization data">
        <EmployeeDashboardGrid columns="wide">
          <EmployeeSummaryCard label="Reports to" value={supervisor.status === 'loaded' ? displayName(supervisor.profile) : (supervisorId > 0 ? 'Restricted or unavailable' : 'Unassigned')} detail="employee_profiles.supervisor_employee_id" />
          <EmployeeSummaryCard label="Direct reports" value={reports.status === 'loaded' ? String(directReports.length) : '—'} detail="Authoritative reports-to edges only" />
          <EmployeeSummaryCard label="Department" value={profile?.department || 'Not recorded'} detail="Employee profile attribute" />
          <EmployeeSummaryCard label="Location" value={currentLocation || 'Not recorded'} detail="Employee or linked manager location" />
        </EmployeeDashboardGrid>
        <p className="employee-workspace__scope-note">Shared location, department, title, schedule, and goal allocation do not create reporting relationships.</p>
      </EmployeeWorkspaceSection>

      <EmployeeWorkspaceSection title="Reporting structure" description="Current authoritative relationship only">
        {supervisor.status === 'loading' ? <LoadingSkeleton variant="paragraph" lines={2} /> : null}
        {supervisor.status === 'error' ? <StatusBanner kind="error" message={supervisor.error} /> : null}
        {supervisor.status === 'forbidden' ? <EmployeeWorkspaceEmptyState title="Reporting manager is restricted" description="The relationship exists, but this viewer is not authorized to open that employee profile." release="" /> : null}
        {supervisor.status === 'loaded' && supervisor.profile ? (
          <EmployeeDetailList items={[
            { label: 'Employee', value: displayName(profile) },
            { label: 'Reports to', value: displayName(supervisor.profile) },
            { label: 'Relationship source', value: `Employee profile #${profileId} → supervisor profile #${supervisorId}` }
          ]} />
        ) : null}
        {supervisor.status === 'idle' ? <EmployeeWorkspaceEmptyState title="No reporting manager recorded" description="This employee profile has no authoritative supervisor relationship." release="" /> : null}
      </EmployeeWorkspaceSection>

      <EmployeeWorkspaceSection title="Direct reports" description="Employees whose supervisorEmployeeId equals this employee profile ID">
        {reports.status === 'loading' || locationsLoading ? <LoadingSkeleton variant="paragraph" lines={4} /> : null}
        {reports.status === 'error' || reports.status === 'forbidden' ? <StatusBanner kind="error" message={reports.error} /> : null}
        {reports.status === 'loaded' && directReports.length === 0 ? <EmployeeWorkspaceEmptyState title="No direct reports recorded" description="No authorized employee profile currently points to this employee as supervisor." release="" /> : null}
        {reports.status === 'loaded' && directReports.length ? (
          <ul className="employee-performance__records" aria-label="Authoritative direct reports">
            {directReports.map(report => (
              <li key={report.id}>
                <strong><Link to={buildPersonWorkspacePath({ employeeProfileId: report.id, tab: 'organization' })}>{displayName(report)}</Link></strong>
                <span>{report.jobTitle || report.role || 'Title not recorded'} · {report.department || 'Department not recorded'}</span>
                <span>{locationNames.get(resolvedLocationId(report)) || 'Location not recorded'} · Employee ID: {report.employeeNumber || 'Not recorded'}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </EmployeeWorkspaceSection>

      <EmployeeDashboardGrid>
        <EmployeeWorkspaceSection title="Team composition" description="Real attributes on authorized direct reports">
          {directReports.length ? <EmployeeDetailList items={[
            { label: 'Locations represented', value: locations.length ? locations.join(', ') : 'Not recorded' },
            { label: 'Departments represented', value: departments.length ? departments.join(', ') : 'Not recorded' },
            { label: 'Roles / titles', value: [...new Set(directReports.map(item => item.jobTitle || item.role).filter(Boolean))].join(', ') || 'Not recorded' }
          ]} /> : <EmployeeWorkspaceEmptyState title="Team composition unavailable" description="Composition requires authoritative, authorized direct-report records." release="" />}
        </EmployeeWorkspaceSection>
        <EmployeeWorkspaceSection title="Span of control" description="Direct-report count only">
          <EmployeeSummaryCard label="Current direct reports" value={reports.status === 'loaded' ? String(directReports.length) : '—'} detail="No ideal-span or manager-effectiveness score is calculated" />
        </EmployeeWorkspaceSection>
      </EmployeeDashboardGrid>

      <EmployeeWorkspaceSection title="Team operational context" description="Scope boundary">
        <EmployeeWorkspaceEmptyState title="Employee team KPIs are not available" description="Existing operational metrics are manager or location context and are not attributed to employees or converted into a team performance score." release="Not modeled" />
      </EmployeeWorkspaceSection>

      <EmployeeDashboardGrid>
        <EmployeeWorkspaceSection title="Team risks / attention" description="No authoritative model">
          <EmployeeWorkspaceEmptyState title="Team risk intelligence is planned" description="No risk count, high-performer count, turnover prediction, or manager score is generated." release="Planned" />
        </EmployeeWorkspaceSection>
        <EmployeeWorkspaceSection title="Organization history" description="No reporting-history model">
          <EmployeeWorkspaceEmptyState title="Organization history is planned" description="The repository stores the current reports-to edge but no effective-dated reporting history." release="Planned" />
        </EmployeeWorkspaceSection>
      </EmployeeDashboardGrid>

      <EmployeeWorkspaceSection title="Future AI team intelligence" description="Placeholder only — no AI processing is active">
        <EmployeeWorkspaceEmptyState title="AI team intelligence is planned" description="No effectiveness score, flight risk, succession recommendation, ranking, or employment recommendation is generated." release="Future release" />
      </EmployeeWorkspaceSection>
    </div>
  );
}
